import { useState } from 'react'

import axios from 'axios'
import mixpanel from 'mixpanel-browser'

import { deleteObservation } from '../../../../../../../../api/observations'
import { areQualityFeaturesEnabled } from '../../../../../../../../config/qualityFeatures'
import { useBuildingContext } from '../../../../../../../../contexts/buildingContext'
import { useNotifications } from '../../../../../../../../contexts/notificationProvider'
import { useTagContext } from '../../../../../../../../contexts/tagContext'
import { useUserContext } from '../../../../../../../../contexts/userContext'
import { usePannellumViewer } from '../../../../../../../../hooks/usePannellum'
import { ConfirmationModal } from '../../../../../../../common/Confirmation/Confirmation'
import {
  isAxios404,
  resolveTagObservationId,
} from '../../../../../utils/safetyTagScanMatch'
import { ContextMenuContainer, MenuItem } from '../styles'

const BASE_URL = process.env.REACT_APP_SERVICES_API

/** Above fullscreen panorama (1200) and hotspot context menu (9999). */
const CONFIRM_MODAL_Z_INDEX = 13000

interface PendingDeleteTag {
  id: number
  type: string
  observation?: number | string | null
}

export { resolveTagObservationId }

interface IHotspotContextMenuProps {
  viewerId: string
  xPos: string
  yPos: string
  showMenu: boolean
  /** Id from the right-clicked hotspot DOM; pannellum never fires contextMenuHandler. */
  hotspotTagId?: number | null
}
export const HotspotContextMenu = ({
  viewerId,
  xPos,
  yPos,
  showMenu,
  hotspotTagId = null,
}: IHotspotContextMenuProps) => {
  const { addNotification } = useNotifications()
  const { state: tagState, findSetCurrentTag } = useTagContext()
  const { state: userState } = useUserContext()
  const { state: buildingState } = useBuildingContext()
  const [confirmation, setConfirmation] = useState<boolean>(false)
  const [pendingDelete, setPendingDelete] = useState<PendingDeleteTag | null>(
    null,
  )
  const { removeHotSpot } = usePannellumViewer(viewerId)

  const currentTag = tagState.tags.current
  const isQualityTag =
    areQualityFeaturesEnabled &&
    (pendingDelete?.type === 'QUALITY' || currentTag?.type === 'QUALITY')
  const isObservationTag =
    pendingDelete?.type === 'SAFETY' ||
    currentTag?.type === 'SAFETY' ||
    isQualityTag

  const resolveTagForDelete = () => {
    const candidates = [hotspotTagId, currentTag?.id].filter(
      (id): id is number => typeof id === 'number' && Number.isFinite(id),
    )
    for (const id of candidates) {
      const fromAll = (tagState.tags.all as { id?: unknown }[]).find(
        item => String(item.id) === String(id),
      )
      if (fromAll?.id != null) {
        return fromAll as {
          id: number
          type: string
          observation?: unknown
          observation_id?: unknown
        }
      }
    }
    if (currentTag?.id != null) {
      return currentTag as {
        id: number
        type: string
        observation?: unknown
        observation_id?: unknown
      }
    }
    if (hotspotTagId != null) {
      // Prefer known current tag type; do not invent SAFETY for QUALITY pins.
      return {
        id: hotspotTagId,
        type: (currentTag?.type as string) || 'SAFETY',
        observation: currentTag?.observation,
        observation_id: currentTag?.observation_id,
      }
    }
    return null
  }

  const openDeleteConfirmation = () => {
    const tag = resolveTagForDelete()
    if (!tag?.id) {
      addNotification('Tag could not be removed', 'error')
      return
    }
    findSetCurrentTag(tag.id, true)
    setPendingDelete({
      id: tag.id,
      type: tag.type,
      observation: resolveTagObservationId(tag),
    })
    setConfirmation(true)
  }

  const destroyHotSpot = async () => {
    const tag =
      pendingDelete ??
      (() => {
        const resolved = resolveTagForDelete()
        return resolved?.id
          ? {
              id: resolved.id as number,
              type: resolved.type as string,
              observation: resolveTagObservationId(resolved),
            }
          : null
      })()

    if (!tag?.id) {
      addNotification('Tag could not be removed', 'error')
      setConfirmation(false)
      return
    }

    const isObservationBacked =
      tag.type === 'SAFETY' ||
      (areQualityFeaturesEnabled && tag.type === 'QUALITY')
    let observationDeleted = false

    try {
      if (isObservationBacked && tag.observation && buildingState.projectId) {
        try {
          await deleteObservation(buildingState.projectId, tag.observation)
          observationDeleted = true
        } catch (obsErr) {
          // Orphaned pin: earlier destroy may have removed the row already.
          if (!isAxios404(obsErr)) {
            addNotification('Observation could not be deleted', 'error')
            setConfirmation(false)
            return
          }
          observationDeleted = true
        }
      } else if (isObservationBacked && !tag.observation) {
        addNotification(
          'Tag removed; linked observation id was missing',
          'warning',
        )
      }

      try {
        await axios.patch(`${BASE_URL}/tags/${tag.id}/`, {
          deleted: true,
        })
      } catch (_tagErr) {
        addNotification(
          observationDeleted
            ? 'Observation deleted but tag could not be removed'
            : 'Tag could not be removed',
          'error',
        )
        setConfirmation(false)
        return
      }

      removeHotSpot(tag.id)
      if (!isObservationBacked || tag.observation) {
        addNotification(
          isObservationBacked ? 'Observation deleted' : 'Tag removed',
          'success',
        )
      }
      mixpanel.track('Remove Tag')
      setPendingDelete(null)
      setConfirmation(false)
    } catch (_e) {
      addNotification(
        isObservationBacked
          ? 'Observation could not be deleted'
          : 'Tag could not be removed',
        'error',
      )
      setConfirmation(false)
    }
  }

  if (currentTag?.type === 'CHAT') {
    if (currentTag.chat_room?.admin?.public_id !== userState.public_id) {
      return <></>
    }
  }

  return (
    <>
      <ContextMenuContainer
        style={{
          top: yPos,
          left: xPos,
          display: showMenu ? 'block' : 'none',
        }}
      >
        <ul>
          <MenuItem
            onClickCapture={openDeleteConfirmation}
            className="pnlm-ignore context-menu-li"
          >
            {currentTag?.type === 'CHAT' ? 'Archive' : 'Delete'}
          </MenuItem>
        </ul>
      </ContextMenuContainer>
      <ConfirmationModal
        isOpen={confirmation}
        setIsOpen={open => {
          setConfirmation(open)
          if (!open) setPendingDelete(null)
        }}
        message={
          isObservationTag
            ? 'Are you sure you want to delete this observation?'
            : 'Are you sure you want to archive this tag?'
        }
        onConfirm={destroyHotSpot}
        closeOnConfirm={false}
        styleOverrides={{
          overlay: { zIndex: CONFIRM_MODAL_Z_INDEX },
          content: { zIndex: CONFIRM_MODAL_Z_INDEX + 1 },
        }}
      />
    </>
  )
}
